import { localized } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { createColumnHelper, createMeta, createRowActions } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { getColorByString } from "@msuite/utils/colors";
import { moment } from "@msuite/utils/date";
import { formatNumber } from "@msuite/utils/numbers";
import { getMeasurementsString, guardString } from "@msuite/utils/strings";
import type { Vehicle } from "@msuite/utils/types";
import { WebfleetIntegration } from "@/components/vehicles";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<Vehicle>();

export const columns = [
	columnHelper.accessor("licensePlate", {
		id: "licensePlate",
		header: localized("vehicles.license_plate"),
		cell: (cell) => (
			<div className="h-stack">
				<StatusIndicator color="blue" />
				{cell.getValue()}
			</div>
		),
	}),
	columnHelper.accessor((row) => row._integrations, {
		id: "integration",
		header: localized("integrations.integration"),
		cell: (cell) => (
			<div className="absolute inset-0 justify-end px-2 w-full h-stack">
				<WebfleetIntegration
					vehicleId={cell.row.original.id}
					vehicleIntegrations={cell.getValue()}
				/>
			</div>
		),
	}),
	columnHelper.accessor("type", {
		id: "type",
		header: localized("general.type"),
		cell: (cell) =>
			cell.getValue()
				? guardString(localized(`vehicles.types.${cell.getValue()}`))
				: guardString(null),
	}),
	columnHelper.accessor("manufacturer", {
		id: "manufacturer",
		header: localized("vehicles.manufacturer"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor(
		(row) => (row.fuelType ? localized(`vehicles.fuel_types.${row.fuelType}`) : guardString(null)),
		{
			id: "fuelType",
			header: localized("vehicles.fuel_type"),
			cell: (cell) => (
				<Tag
					variant="plain"
					color={getColorByString(cell.getValue())}
				>
					{cell.getValue()}
				</Tag>
			),
		},
	),
	columnHelper.accessor("vin", {
		id: "vin",
		header: localized("vehicles.vin"),
		cell: (cell) => guardString(cell.getValue()),
		meta: createMeta({ mono: true }),
	}),
	columnHelper.accessor("firstRegistration", {
		id: "firstRegistration",
		header: localized("vehicles.first_registration"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString(null)),
	}),
	columnHelper.accessor((row) => getMeasurementsString({ ...row }), {
		id: "vehicleDimensions",
		header: localized("vehicles.dimensions"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor(
		(row) =>
			getMeasurementsString({
				width: row.flatbedWidth,
				depth: row.flatbedDepth,
			}),
		{
			header: localized("vehicles.flatbed"),
			cell: (cell) =>
				guardString(
					getMeasurementsString({
						width: cell.row.original.flatbedWidth,
						depth: cell.row.original.flatbedDepth,
					}),
				),
		},
	),
	columnHelper.accessor("maximumLoadKg", {
		id: "maximumLoadKg",
		header: localized("vehicles.maximum_load"),
		cell: (cell) => formatNumber(cell.getValue(), "kilogram"),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor(
		(row) =>
			row._cranes.map((c) => localized(`vehicles.${c.position}_crane`)).join(", ") ||
			guardString(null),
		{
			id: "cranes",
			header: localized("vehicles.cranes"),
			cell: (cell) => cell.getValue(),
		},
	),
	createRowActions(RowActions),
];
